import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, serverTimestamp, doc, setDoc, increment } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { emailSchema, phoneSchema, sourceSchema, generalInquirySchema, sponsorSchema, creatorRequestSchema, creatorApplicationSchema, boatSlipSchema, artistUploadSchema } from "@/lib/validation";
import { getReferral } from "@/lib/referral";

const firebaseConfig = {
  apiKey: "AIzaSyDgBvXbenzjIyw-cRsCs3xJVqcsBNLAeIw",
  authDomain: "yrd-2026.firebaseapp.com",
  projectId: "yrd-2026",
  storageBucket: "yrd-2026.firebasestorage.app",
  messagingSenderId: "768567693507",
  appId: "1:768567693507:web:496a27b1694bf130eb1276",
  measurementId: "G-658RT113W0"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firestore with named database
export const db = getFirestore(app, "yrd2026");

// Cloud Storage (used for artist photo uploads)
export const storage = getStorage(app);

// Helper functions for form submissions with validation
export const saveEmail = async (email: string, source: "general" | "sponsor" | "stay-in-know" | "early-access" | "hotel-packages" | "ticket-notify" | "email-blast") => {
  const validatedEmail = emailSchema.parse(email);
  const validatedSource = sourceSchema.parse(source);
  const ref = getReferral();
  return addDoc(collection(db, "emails"), {
    email: validatedEmail,
    source: validatedSource,
    ...(ref && { ref }),
    createdAt: serverTimestamp()
  });
};

export const savePhoneNumber = async (phone: string, source: "general" | "sponsor" | "stay-in-know" | "early-access" | "hotel-packages" | "ticket-notify" | "email-blast") => {
  const validatedPhone = phoneSchema.parse(phone);
  const validatedSource = sourceSchema.parse(source);
  const ref = getReferral();
  return addDoc(collection(db, "phone_numbers"), {
    phone: validatedPhone,
    source: validatedSource,
    ...(ref && { ref }),
    createdAt: serverTimestamp()
  });
};

export const saveGeneralInquiry = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}) => {
  const validated = generalInquirySchema.parse(data);
  return addDoc(collection(db, "general_inquiries"), {
    ...validated,
    createdAt: serverTimestamp()
  });
};

export const saveCreatorRequest = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  instagram: string;
  tickets: number;
  charity?: boolean;
  charityTickets?: number;
}) => {
  const validated = creatorRequestSchema.parse(data);
  return addDoc(collection(db, "creator_requests"), {
    ...validated,
    source: "creator-request",
    createdAt: serverTimestamp()
  });
};

export const saveCreatorApplication = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  instagram: string;
  dob: string;
}) => {
  const validated = creatorApplicationSchema.parse(data);
  const ref = getReferral();
  return addDoc(collection(db, "creator_applications"), {
    ...validated,
    source: "creator-apply",
    ...(ref && { ref }),
    createdAt: serverTimestamp()
  });
};

export const saveSponsor = async (data: {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  message: string;
}) => {
  const validated = sponsorSchema.parse(data);
  return addDoc(collection(db, "sponsors"), {
    ...validated,
    createdAt: serverTimestamp()
  });
};

export const saveBoatSlip = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  hasPasses: boolean;
  hasAdmiralPass: boolean;
  partySize: number;
  boatSize: string;
}) => {
  const validated = boatSlipSchema.parse(data);
  const ref = getReferral();
  return addDoc(collection(db, "boat_slips"), {
    ...validated,
    source: "boat-slip",
    ...(ref && { ref }),
    createdAt: serverTimestamp()
  });
};

// ── Artist photo uploads (/artist-upload) ─────────────────────────
// Bands drop their promo photos here. Each file streams to Cloud Storage
// under artist-uploads/{submissionId}/ with a live progress callback, then we
// write ONE Firestore doc (artist_uploads) holding the display name + the list
// of uploaded files (download URL + metadata). The team dashboard reads that.

export type UploadedFile = { name: string; url: string; path: string; size: number; type: string };

// Map common photo/video extensions → MIME type. Phones sometimes report an
// empty file.type (esp. some .mov files), which would upload as octet-stream and
// get rejected by the Storage rule (it requires image/* or video/*). Infer from
// the extension so those still go through.
const EXT_MIME: Record<string, string> = {
  mov: "video/quicktime", mp4: "video/mp4", m4v: "video/x-m4v", webm: "video/webm",
  avi: "video/x-msvideo", mkv: "video/x-matroska", "3gp": "video/3gpp", mpeg: "video/mpeg", mpg: "video/mpeg",
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", gif: "image/gif", webp: "image/webp",
  heic: "image/heic", heif: "image/heif",
};
const resolveType = (file: File): string =>
  file.type || EXT_MIME[file.name.split(".").pop()?.toLowerCase() || ""] || "application/octet-stream";

// Upload a single file with progress. Resumable so big phone photos/videos
// survive a flaky mobile connection. Returns the metadata we persist in Firestore.
export const uploadArtistFile = (
  submissionId: string,
  file: File,
  onProgress?: (pct: number) => void
): Promise<UploadedFile> => {
  // Keep the original name but strip anything that could break a storage path.
  const safeName = file.name.replace(/[^\w.\-() ]/g, "_").slice(-200) || "upload";
  const path = `artist-uploads/${submissionId}/${Date.now()}-${safeName}`;
  const contentType = resolveType(file);
  const task = uploadBytesResumable(storageRef(storage, path), file, { contentType });
  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ name: file.name.slice(0, 300), url, path, size: file.size, type: contentType });
        } catch (e) {
          reject(e);
        }
      }
    );
  });
};

export const saveArtistUpload = async (data: {
  displayName: string;
  email?: string;
  files: UploadedFile[];
}) => {
  const validated = artistUploadSchema.parse(data);
  return addDoc(collection(db, "artist_uploads"), {
    displayName: validated.displayName,
    ...(validated.email ? { email: validated.email } : {}),
    files: validated.files,
    fileCount: validated.files.length,
    source: "artist-upload",
    createdAt: serverTimestamp(),
  });
};

// ── n8n Webhook Helpers (dual-write) ──────────────────────────────

const N8N_BASE = "https://n8n-roostertail-u60710.vm.elestio.app/webhook";
const N8N_BASE_SS = "https://n8n-roostertail-u60710.vm.elestio.app/webhook";

export const n8nSaveEmail = async (email: string, source: string) => {
  const validatedEmail = emailSchema.parse(email);
  const ref = getReferral();
  return fetch(`${N8N_BASE}/yrd-waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: validatedEmail, source, ...(ref && { ref }) }),
  });
};

export const n8nSavePhone = async (phone: string, source: string) => {
  const validatedPhone = phoneSchema.parse(phone);
  const ref = getReferral();
  return fetch(`${N8N_BASE}/yrd-phone`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: validatedPhone, source, ...(ref && { ref }) }),
  });
};

export const n8nSaveInquiry = async (data: {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  message: string;
}) => {
  const validated = generalInquirySchema.parse(data);
  return fetch(`${N8N_BASE}/yrd-inquiry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated),
  });
};

export const n8nSaveSponsor = async (data: {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  company: string;
  message: string;
}) => {
  const validated = sponsorSchema.parse(data);
  return fetch(`${N8N_BASE}/yrd-sponsor`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated),
  });
};

// ── Second n8n Webhook Set (SS) ──────────────────────────────

export const n8nSaveEmailSS = async (email: string, source: string) => {
  const validatedEmail = emailSchema.parse(email);
  const ref = getReferral();
  return fetch(`${N8N_BASE_SS}/yrd-waitlist-ss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: validatedEmail, source, ...(ref && { ref }) }),
  });
};

export const n8nSavePhoneSS = async (phone: string, source: string) => {
  const validatedPhone = phoneSchema.parse(phone);
  const ref = getReferral();
  return fetch(`${N8N_BASE_SS}/yrd-phone-ss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: validatedPhone, source, ...(ref && { ref }) }),
  });
};

export const n8nSaveInquirySS = async (data: {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  message: string;
}) => {
  const validated = generalInquirySchema.parse(data);
  return fetch(`${N8N_BASE_SS}/yrd-inquiry-ss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated),
  });
};

export const n8nSaveSponsorSS = async (data: {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  company: string;
  message: string;
}) => {
  const validated = sponsorSchema.parse(data);
  return fetch(`${N8N_BASE_SS}/yrd-sponsor-ss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated),
  });
};

// ── SlickText Phone Webhook ──────────────────────────────

const stripPhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, "");
  return digits.startsWith("1") && digits.length === 11 ? digits.slice(1) : digits;
};

export const n8nSavePhoneSlickText = async (phone: string) => {
  const validatedPhone = phoneSchema.parse(phone);
  return fetch(`${N8N_BASE}/yrd-phone-slicktext`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: stripPhone(validatedPhone) }),
  });
};

// 4th of July campaign: dedicated SlickText workflow that texts the
// "6 tickets for the price of 4" offer + ticket link. Separate from the
// generic waitlist welcome so we can turn it off after the holiday by
// flipping JULY4_ENABLED in EarlyAccessModal (this call just no-ops if the
// n8n webhook is inactive).
export const n8nSaveFourthTickets = async (phone: string) => {
  const validatedPhone = phoneSchema.parse(phone);
  return fetch(`${N8N_BASE}/yrd-fourth-tickets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: stripPhone(validatedPhone) }),
  });
};

// Giveaway (1-in-50 → 2 free GA): fire the win/lose roll + SMS via the
// dedicated n8n workflow. Win logic lives server-side (never client-side).
export const n8nSaveGiveaway = async (phone: string) => {
  const validatedPhone = phoneSchema.parse(phone);
  return fetch(`${N8N_BASE}/yrd-giveaway`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: stripPhone(validatedPhone) }),
  });
};

// Giveaway entry record (name + email + phone) → Firestore system of record.
export const saveGiveawayEntry = async (data: { phone: string; firstName: string; email: string }) => {
  const validatedEmail = emailSchema.parse(data.email);
  const validatedPhone = phoneSchema.parse(data.phone);
  const ref = getReferral();
  return addDoc(collection(db, "giveaway_entries"), {
    firstName: data.firstName.trim().slice(0, 80),
    email: validatedEmail,
    phone: validatedPhone,
    source: "giveaway-1in50",
    ...(ref && { ref }),
    createdAt: serverTimestamp(),
  });
};

// Pop-up funnel counters for the team dashboard. Atomically increments per-day
// aggregate fields on popup_stats/{YYYY-MM-DD Detroit} so the dashboard reads a
// handful of docs (not one doc per impression). Fire-and-forget; never blocks UI.
const detroitDay = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Detroit" }).format(new Date());

export const recordPopupStat = async (fields: string[]) => {
  if (!fields.length) return;
  const day = detroitDay();
  const data: Record<string, unknown> = { day, updatedAt: serverTimestamp() };
  for (const f of fields) data[f] = increment(1);
  try {
    await setDoc(doc(db, "popup_stats", day), data, { merge: true });
  } catch (e) {
    console.error("recordPopupStat:", e);
  }
};

// Creator Crew: fire the SlickText "thanks for joining" text on /creators signup.
export const n8nSendCreatorText = async (data: { phone: string; firstName?: string; instagram?: string }) => {
  const validatedPhone = phoneSchema.parse(data.phone);
  return fetch(`${N8N_BASE}/yrd-creator-text`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone: stripPhone(validatedPhone), firstName: data.firstName, instagram: data.instagram }),
  });
};

// Meta Conversions API helper
export const sendMetaLeadEvent = async (
  data: { email?: string; phone?: string },
  source: string = "early-access"
) => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  if (!projectId) {
    console.error("Missing VITE_SUPABASE_PROJECT_ID for Meta Conversions API");
    return;
  }
  const url = `https://${projectId}.supabase.co/functions/v1/meta-conversions`;
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...data, source }),
  });
};
